import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ── Bring-your-own-app setup ────────────────────────────────────────────────
// Mirrors instagramSlice.js exactly, for the parallel meta-facebook module.
export const fetchUrls = createAsyncThunk('facebook/fetchUrls', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/urls');
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch setup URLs'); }
});

export const fetchAppCredentials = createAsyncThunk('facebook/fetchAppCredentials', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/credentials');
    return res.data.data.credential;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch app credentials'); }
});

export const saveAppCredentials = createAsyncThunk('facebook/saveAppCredentials', async (payload, { rejectWithValue }) => {
  try {
    const res = await api.put('/meta-facebook/credentials', payload);
    return res.data.data.credential;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to save app credentials'); }
});

// ── Account (connect/disconnect) ───────────────────────────────────────────
export const fetchFacebookAccount = createAsyncThunk('facebook/fetchAccount', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/account');
    return res.data.data.account;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch account'); }
});

// A tenant can have several connected Pages now — this is what the settings
// page and comment-moderation page selector actually list from.
export const fetchFacebookAccounts = createAsyncThunk('facebook/fetchAccounts', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/accounts');
    return res.data.data.accounts;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch connected pages'); }
});

export const startConnect = createAsyncThunk('facebook/startConnect', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/oauth/start');
    return res.data.data.url;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to start connection'); }
});

export const connectWithAccessToken = createAsyncThunk('facebook/connectWithAccessToken', async (accessToken, { rejectWithValue }) => {
  try {
    const res = await api.post('/meta-facebook/oauth/connect-token', { accessToken });
    return res.data.data; // { status: 'connected', account } or { status: 'choose_page', pendingId, pages }
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to connect with that access token'); }
});

export const fetchPendingPages = createAsyncThunk('facebook/fetchPendingPages', async (pendingId, { rejectWithValue }) => {
  try {
    const res = await api.get(`/meta-facebook/oauth/pending/${pendingId}`);
    return res.data.data.pages;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Connection session expired'); }
});

export const finalizeConnect = createAsyncThunk('facebook/finalizeConnect', async ({ pendingId, pageId, pageIds }, { rejectWithValue }) => {
  try {
    const res = await api.post('/meta-facebook/oauth/finalize', { pendingId, pageIds: pageIds || [pageId] });
    return res.data.data.account;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to finalize connection'); }
});

// pageId is required now that a tenant can have more than one connected Page.
export const disconnectAccount = createAsyncThunk('facebook/disconnect', async (pageId, { rejectWithValue }) => {
  try {
    await api.post('/meta-facebook/disconnect', { pageId });
    return pageId;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to disconnect'); }
});

// ── Comments ────────────────────────────────────────────────────────────────
export const fetchComments = createAsyncThunk('facebook/fetchComments', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-facebook/comments', { params });
    return { comments: res.data.data, posts: res.data.meta?.posts || {} };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch comments'); }
});

export const syncPosts = createAsyncThunk('facebook/syncPosts', async (pageId, { rejectWithValue }) => {
  try {
    const res = await api.post('/meta-facebook/comments/sync-posts', { pageId });
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to sync posts'); }
});

export const replyToComment = createAsyncThunk('facebook/replyToComment', async ({ commentId, message, asPrivateReply }, { rejectWithValue }) => {
  try {
    await api.post(`/meta-facebook/comments/${commentId}/reply`, { message, asPrivateReply });
    return { commentId, message };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to reply'); }
});

export const hideComment = createAsyncThunk('facebook/hideComment', async ({ commentId, hide }, { rejectWithValue }) => {
  try {
    await api.post(`/meta-facebook/comments/${commentId}/hide`, { hide });
    return { commentId, hide };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to update comment'); }
});

export const deleteComment = createAsyncThunk('facebook/deleteComment', async (commentId, { rejectWithValue }) => {
  try {
    await api.delete(`/meta-facebook/comments/${commentId}`);
    return commentId;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to delete comment'); }
});

// ── Slice ─────────────────────────────────────────────────────────────────
const facebookSlice = createSlice({
  name: 'facebook',
  initialState: {
    urls: null,
    credential: null,
    credentialLoading: false,
    account: null,
    accounts: [],
    accountLoading: false,
    pendingPages: [],
    connectUrl: null,
    comments: [],
    commentsPosts: {},
    commentsMeta: {},
    loading: false,
    error: null,
    // Cross-tab "new activity" badges for the Facebook Workspace's horizontal
    // nav — bumped by a socket listener that stays mounted across all tabs
    // (FacebookDashboardLayout), cleared when the user actually opens that tab.
    unreadDMs: 0,
    unreadComments: 0,
  },
  reducers: {
    clearFacebookError: (state) => { state.error = null; },
    clearConnectUrl: (state) => { state.connectUrl = null; },
    bumpUnreadDMs: (state) => { state.unreadDMs += 1; },
    bumpUnreadComments: (state) => { state.unreadComments += 1; },
    clearUnreadDMs: (state) => { state.unreadDMs = 0; },
    clearUnreadComments: (state) => { state.unreadComments = 0; },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      // Bring-your-own-app setup
      .addCase(fetchUrls.fulfilled, (state, action) => { state.urls = action.payload; })
      .addCase(fetchAppCredentials.pending, (state) => { state.credentialLoading = true; })
      .addCase(fetchAppCredentials.fulfilled, (state, action) => { state.credentialLoading = false; state.credential = action.payload; })
      .addCase(fetchAppCredentials.rejected, (state) => { state.credentialLoading = false; })
      .addCase(saveAppCredentials.fulfilled, (state, action) => { state.credential = action.payload; })
      .addCase(saveAppCredentials.rejected, rejected)

      // Account
      .addCase(fetchFacebookAccount.pending, (state) => { state.accountLoading = true; })
      .addCase(fetchFacebookAccount.fulfilled, (state, action) => { state.accountLoading = false; state.account = action.payload; })
      .addCase(fetchFacebookAccount.rejected, (state) => { state.accountLoading = false; })
      .addCase(fetchFacebookAccounts.pending, (state) => { state.accountLoading = true; })
      .addCase(fetchFacebookAccounts.fulfilled, (state, action) => { state.accountLoading = false; state.accounts = action.payload; })
      .addCase(fetchFacebookAccounts.rejected, (state) => { state.accountLoading = false; })
      .addCase(startConnect.fulfilled, (state, action) => { state.connectUrl = action.payload; })
      .addCase(startConnect.rejected, rejected)
      .addCase(fetchPendingPages.fulfilled, (state, action) => { state.pendingPages = action.payload; })
      .addCase(fetchPendingPages.rejected, rejected)
      .addCase(connectWithAccessToken.fulfilled, (state, action) => {
        if (action.payload.status === 'connected') {
          state.account = action.payload.account;
          state.pendingPages = [];
        } else {
          state.pendingPages = action.payload.pages;
        }
      })
      .addCase(connectWithAccessToken.rejected, rejected)
      .addCase(finalizeConnect.fulfilled, (state, action) => {
        state.account = action.payload;
        state.pendingPages = [];
        // Newly finalized page joins the list rather than replacing it —
        // connecting a second/third Page is additive now.
        const idx = state.accounts.findIndex((a) => a.pageId === action.payload.pageId);
        if (idx >= 0) state.accounts[idx] = action.payload;
        else state.accounts.push(action.payload);
      })
      .addCase(finalizeConnect.rejected, rejected)
      .addCase(disconnectAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.filter((a) => a.pageId !== action.payload);
        if (state.account?.pageId === action.payload) state.account = null;
      })

      // Comments
      .addCase(fetchComments.pending, pending)
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = action.payload.comments;
        state.commentsPosts = action.payload.posts;
      })
      .addCase(fetchComments.rejected, rejected)
      .addCase(replyToComment.fulfilled, (state, action) => {
        const c = state.comments.find((x) => x.commentId === action.payload.commentId);
        if (c) { c.repliedAt = new Date().toISOString(); c.replyText = action.payload.message; }
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const c = state.comments.find((x) => x.commentId === action.payload.commentId);
        if (c) c.hidden = action.payload.hide;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(
          (x) => x.commentId !== action.payload && x.parentCommentId !== action.payload
        );
      });
  },
});

export const {
  clearFacebookError, clearConnectUrl,
  bumpUnreadDMs, bumpUnreadComments, clearUnreadDMs, clearUnreadComments,
} = facebookSlice.actions;
export default facebookSlice.reducer;
